import { useState, useEffect, useRef } from "react";

// Define types for image coordinates
interface ImageCoordinate {
  point: [number, number];
  label: string;
}

// Reactive context shared between a page and the Header's guided-walkthrough
// menu. The page publishes whether the prerequisites for a "How to Vijilize"
// tour are met; the Header reads them to decide whether to open Add modals.
interface WalkthroughState {
  page: string;
  hasSourcePatent: boolean;
  hasTarget: boolean;
  // Whether an Add-by-patent-number modal is currently open on the page. Lets
  // the Header tell a successful add (prerequisite flips) from a cancel (modal
  // closes with the prerequisite still unmet) when gating "How to Vijilize".
  addPatentModalOpen: boolean;
}

// Imperative actions a page registers so the Header can trigger them (e.g.
// open the Add Source / Add Comparison modals) while gating a walkthrough.
interface WalkthroughActions {
  openAddSourcePatent?: () => void;
  openAddCompareTarget?: () => void;
  // Switch the Compare With pane to the URLs sub-tab so the per-URL monitor
  // icon is rendered (used by the "Configure Product Monitoring" walkthrough).
  openCompareUrlsTab?: () => void;
  // Expand the first covered-product target on the Vijilization detail page so
  // the "View More Details" / citation anchors render (used by the "Reading a
  // Vijilization" walkthrough).
  expandFirstCoveredProduct?: () => void;
}

const useVijilize = () => {
  const [headerData, setHeaderData] = useState(() => {
    const storedData = localStorage.getItem("headerData");
    return storedData && storedData !== "undefined"
      ? JSON.parse(storedData)
      : {
          projectDetail: { title: "", description: "", id: "" },
          claimAnalysis: { title: "", id: "" },
          vijilize: { title: "", id: "" },
          claimChart: { title: "", id: "" },
          portfolioDetail: { title: "", description: "", id: "" },
        };
  });

  const [isActiveSection, setIsActiveSection] = useState(0);
  const [clickedHeatmapData, setClickedHeatmapData] = useState<any>(null);
  const [markdownContent, setMarkdownContent] = useState<any>(null);
  const [imageCoordinates, setImageCoordinates] = useState<ImageCoordinate[]>(
    []
  );
  const [originalImagePath, setOriginalImagePath] = useState<string>("");
  const [annotationS3Path, setAnnotationS3Path] = useState<string>("");
  const [markdownS3Path, setMarkdownS3Path] = useState<string>("");
  const [annotationData, setAnnotationData] = useState<any>(null);
  const [patentImage, setPatentImage] = useState<any>(null);
   const [fontInfo, setFontInfo] = useState<any>(null)

  // Guided-walkthrough coordination state. `walkthroughState` is reactive so
  // the Header re-renders when source/target presence changes; the action
  // callbacks live in a ref so they don't trigger the provider's state diff.
  const [walkthroughState, setWalkthroughState] = useState<WalkthroughState>({
    page: "",
    hasSourcePatent: false,
    hasTarget: false,
    addPatentModalOpen: false,
  });
  const walkthroughActionsRef = useRef<WalkthroughActions>({});
  const registerWalkthroughActions = (actions: WalkthroughActions) => {
    walkthroughActionsRef.current = actions || {};
  };
  const getWalkthroughActions = () => walkthroughActionsRef.current;

  useEffect(() => {
    localStorage.setItem("headerData", JSON.stringify(headerData));
  }, [headerData]);

  const setVijilizeParamsData = (data: any) => {
    localStorage.setItem("vijilizeParamsData", JSON.stringify(data));
  };

  const getVijilizeParamsData = () => {
    const data = localStorage.getItem("vijilizeParamsData");
    return data ? JSON.parse(data) : null;
  };

  const setHeaderDataLocal = (data: any) => {
    setHeaderData(data);
  };

  return {
    getVijilizeParamsData,
    setVijilizeParamsData,
    isActiveSection,
    setIsActiveSection,
    setHeaderDataLocal,
    headerData,
    clickedHeatmapData,
    setClickedHeatmapData,
    markdownContent,
    setMarkdownContent,
    imageCoordinates,
    setImageCoordinates,
    originalImagePath,
    setOriginalImagePath,
    annotationS3Path,
    setAnnotationS3Path,
    markdownS3Path,
    setMarkdownS3Path,
    annotationData,
    setAnnotationData,
    patentImage,
    setPatentImage,
    fontInfo,
    setFontInfo,
    walkthroughState,
    setWalkthroughState,
    registerWalkthroughActions,
    getWalkthroughActions,
  };
};

export default useVijilize;
